import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Box,
    Button,
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Spinner,
    Stack,
    Text,
    Tooltip
} from "@chakra-ui/react";
import {useApolloClient, useMutation, useQuery} from "@apollo/client";
import moment from "moment";
import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {DATE_FORMAT_DA} from "./Versioning";
import {
    areaDiffQuery,
    createDraftFromVersionMutation,
    revertAreaToPublishedMutation,
    versionsQuery
} from "./versionQueries";
import {useConfirmDialog} from "../../components/common/ConfirmDialogProvider";
import {Header, RowData, TableInternal} from "../../components/table/TableInternal";
import {AreaDiff, FieldDiff, VersionDiffModalProps} from "./types";
import {Version} from "../../components/types";

const ZONE_LABELS: Record<string, string> = {
    boundaries: "Boundaries",
    maintenance: "Maintenance",
    entry: "Entry",
    exit: "Exit"
};

const normalizePath = (path: string) => {
    return path.replace(/\[[^\]]*]/g, "").replace(/\.+/g, ".").replace(/^\./, "").replace(/\.$/, "");
};

const SOUND_FILE_VALUE_FIELDS = new Set([
    "fileName",
    "volumeDb",
    "delay",
    "fadeIn",
    "fadeOut",
    "playOnes",
    "synchronize",
    "noSyncTime"
]);

const LAT_LNG_LABELS: Record<string, string> = {
    lat: "Latitude",
    lng: "Longitude"
};

const BEACON_TRIGGER_LABELS: Record<string, string> = {
    macAddr: "MAC address",
    majorId: "Major ID",
    minorId: "Minor ID",
    name: "Name",
    type: "Type",
    zoneId: "Zone"
};

const WIFI_TRIGGER_LABELS: Record<string, string> = {
    name: "Name",
    type: "Type",
    zoneId: "Zone"
};

const GPS_TRIGGER_LABELS: Record<string, string> = {
    shapeType: "Shape type",
    polygonCoords: "Polygon",
    circlePosition: "Circle center",
    circleRadius: "Circle radius",
    name: "Name",
    type: "Type",
    zoneId: "Zone"
};

const TRIGGER_RULE_LABELS: Record<string, string> = {
    eventType: "Event type",
    type: "Type",
    spotKey: "Spot",
    spotId: "Spot",
    zoneKey: "Ambient zone",
    minMsSinceFinished: "Minimum time since fully played (ms)",
    minMsSinceTriggered: "Minimum time since triggered (ms)"
};

const GROUP_MODE_VALUE_LABELS: Record<string, string> = {
    fixed: "Fixed groups",
    autoBySpots: "Automatic assignment by spots",
    userCreated: "User created"
};

const capitalize = (value: string) => {
    if (!value) {
        return value;
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
};

const getLastField = (path: string) => {
    const normalized = normalizePath(path);
    return normalized.split(".").filter(Boolean).pop() || normalized;
};

const getSoundFileDescriptor = (path: string) => {
    const match = path.match(/soundFiles\[([^]]+)]/);
    if (!match) {
        return undefined;
    }
    const raw = match[1];
    const parts = raw.split(":").filter(Boolean);
    if (parts.length === 0) {
        return undefined;
    }
    const language = parts[0];
    const extras = parts.slice(1);
    let variant = "";
    if (extras.includes("ambient")) {
        variant = "Ambient";
    } else if (extras.includes("main")) {
        variant = "Main";
    } else if (extras.length > 0) {
        variant = extras.map(capitalize).join(", ");
    }
    const labelParts = [language, variant].filter(Boolean);
    return labelParts.length > 0 ? labelParts.join(", ") : raw;
};

const getSoundFileKey = (path: string) => {
    const match = path.match(/soundFiles\[([^]]+)]/);
    return match ? match[1] : undefined;
};

// Label maps can set a field to null to render only the base label (no ": Field" suffix).
const buildFieldLabel = (baseLabel: string, field: string | undefined, labels: Record<string, string | null>, hideSuffixFields?: Set<string>) => {
    if (!field) {
        return baseLabel;
    }
    if (hideSuffixFields?.has(field)) {
        return baseLabel;
    }
    const label = labels[field];
    if (label === null) {
        return baseLabel;
    }
    const finalLabel = label || LAT_LNG_LABELS[field] || capitalize(field);
    return `${baseLabel}: ${finalLabel}`;
};

const formatDeepLabel = (baseLabel: string, field?: string, subField?: string, labels?: Record<string, string>) => {
    if (!field) {
        return baseLabel;
    }
    const labelMap = labels || {};
    const fieldLabel = labelMap[field] || LAT_LNG_LABELS[field] || capitalize(field);
    if (!subField) {
        return `${baseLabel}: ${fieldLabel}`;
    }
    const subLabel = labelMap[subField] || LAT_LNG_LABELS[subField];
    if (subLabel) {
        return `${baseLabel}: ${fieldLabel} ${subLabel}`;
    }
    return `${baseLabel}: ${fieldLabel}`;
};

const getFieldLabel = (path: string) => {
    const normalized = normalizePath(path);
    const segments = normalized.split(".").filter(Boolean);
    if (normalized.startsWith("soundFiles")) {
        const lastSegment = getLastField(path);
        const soundFileLabels: Record<string, string | null> = {
            originalFileName: "Original filename",
            fileName: null,
            filePath: "File path",
            ambient: "Ambient",
            language: "Language",
            volumeDb: "Volume",
            delay: "Delay",
            fadeIn: "Fade in",
            fadeOut: "Fade out",
            playOnes: "Play once",
            synchronize: "Synchronize",
            noSyncTime: "No sync time"
        };
        const descriptor = getSoundFileDescriptor(path);
        const baseLabel = descriptor ? `Sound File (${descriptor})` : "Sound File";
        return buildFieldLabel(baseLabel, lastSegment, soundFileLabels);
    }
    if (segments[0] === "beaconTriggers") {
        return formatDeepLabel("Beacon trigger", segments[1], segments[2], BEACON_TRIGGER_LABELS);
    }
    if (segments[0] === "wiFiTriggers") {
        return formatDeepLabel("WiFi trigger", segments[1], segments[2], WIFI_TRIGGER_LABELS);
    }
    if (segments[0] === "gpsTriggers") {
        return formatDeepLabel("GPS trigger", segments[1], segments[2], GPS_TRIGGER_LABELS);
    }
    if (segments[0] === "triggerRules") {
        if (segments[1] === "rules") {
            return formatDeepLabel("Trigger rule", segments[2], segments[3], TRIGGER_RULE_LABELS);
        }
        if (segments[1] === "logic") {
            return "Trigger rules: Logic";
        }
        return "Trigger rules";
    }
    if (segments[0] === "position" || segments[0] === "coords" || segments[0] === "center") {
        return formatDeepLabel("Map position", segments[1], segments[2], LAT_LNG_LABELS);
    }
    if (segments[0] === "polygonCoords") {
        return formatDeepLabel("GPS area", segments[1], segments[2], LAT_LNG_LABELS);
    }
    const lastSegment = getLastField(path);
    const labelMap: Record<string, string> = {
        name: "Name",
        summary: "Summary",
        description: "Description",
        creator: "Creator",
        public: "Publicly available",
        publicAccessCode: "Public access code",
        priority: "Priority",
        beaconSensitivity: "Beacon sensitivity",
        canBeStopped: "Can be stopped",
        synchronizeEntry: "Synchronize entry",
        playMode: "Play mode",
        loop: "Loop",
        runOnes: "Run once",
        playFullAfter: "Play full after",
        resetApps: "Reset apps",
        markCompleted: "Mark completed",
        triggerToActivate: "Trigger to activate",
        showMap: "Show map",
        developerMode: "Developer mode",
        showSoundInfo: "Show sound info",
        showTransport: "Show transport bar",
        groupMode: "Group mode",
        groupModeAutoWindow: "Assignment window (seconds)",
        defaultVolume: "Default volume",
        setDefaultVolumeOnReset: "Set default volume on reset",
        appOverridesSystemVolume: "App overrides system volume",
        disableVolumeButtons: "Disable volume buttons",
        bleScanMode: "BLE scan mode",
        spotTriggerMode: "Spot trigger mode",
        autoPlay: "Auto-play spots",
        discoverMode: "Discover mode",
        label: "Label",
        spotIdentifier: "Spot identifier",
        externalIdentifier: "Spot identifier",
        duckOtherAmbients: "Duck other ambients",
        duckOtherAmbientsDb: "Duck other ambients level",
        stopOtherAmbients: "Stop all ambients",
        radius: "Radius",
        volumeDb: "Volume",
        delay: "Delay",
        fadeIn: "Fade in",
        fadeOut: "Fade out",
        playOnes: "Play once",
        keepPlayingAfterMainFile: "Keep playing ambient after main file",
        synchronize: "Synchronize",
        noSyncTime: "No sync time"
    };
    return labelMap[lastSegment] || "Changed setting";
};

const shouldHideDiff = (path: string) => {
    const normalized = normalizePath(path);
    const lastSegment = getLastField(path);
    return normalized.startsWith("soundFiles") && lastSegment === "originalFileName";
};

const getOriginalFileNameDiff = (changes: FieldDiff[], soundFileKey?: string) => {
    if (!soundFileKey) {
        return undefined;
    }
    return changes.find((change) => {
        return getSoundFileKey(change.path) === soundFileKey
            && getLastField(change.path) === "originalFileName";
    });
};

const getDisplayDiffValues = (diff: FieldDiff, changes: FieldDiff[]) => {
    const normalized = normalizePath(diff.path);
    const lastSegment = getLastField(diff.path);
    if (normalized.startsWith("soundFiles") && lastSegment === "fileName") {
        const originalPath = diff.path.replace(/\.fileName$/, ".originalFileName");
        const originalDiffByPath = changes.find((change) => change.path === originalPath);
        const soundFileKey = getSoundFileKey(diff.path);
        const originalDiff = originalDiffByPath || getOriginalFileNameDiff(changes, soundFileKey);
        if (originalDiff) {
            return {before: originalDiff.before, after: originalDiff.after};
        }
    }
    return {before: diff.before, after: diff.after};
};

const shouldSummarizeValue = (path: string) => {
    const normalized = normalizePath(path);
    if (normalized.startsWith("soundFiles")) {
        const lastSegment = getLastField(path);
        return !SOUND_FILE_VALUE_FIELDS.has(lastSegment);
    }
    return normalized.startsWith("soundFiles")
        || normalized.startsWith("beaconTriggers")
        || normalized.startsWith("wiFiTriggers")
        || normalized.startsWith("gpsTriggers")
        || normalized.startsWith("triggerRules")
        || normalized.startsWith("position")
        || normalized.startsWith("coords")
        || normalized.startsWith("center")
        || normalized.startsWith("polygonCoords");
};

const cleanTechnicalValue = (value: string) => {
    return value
        .replace(/[a-f0-9]{24}/gi, "")
        .replace(/[0-9a-f]{8}-[0-9a-f-]{27}/gi, "")
        .replace(/\bindex-\d+\b/gi, "")
        .replace(/\s{2,}/g, " ")
        .trim();
};

const formatDiffValue = (value: string | undefined, path: string) => {
    if (!value || value === "null") {
        return "-";
    }
    if (value === "Added" || value === "Removed") {
        return value;
    }
    if (shouldSummarizeValue(path)) {
        return "Updated";
    }
    const normalized = value === "true" ? "Yes" : value === "false" ? "No" : value;
    if (getLastField(path) === "groupMode") {
        return normalized
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
            .map((item) => GROUP_MODE_VALUE_LABELS[item] || item)
            .join(", ") || "-";
    }
    const normalizedPath = normalizePath(path);
    const lastSegment = getLastField(path);
    if (normalizedPath.startsWith("soundFiles") && lastSegment === "fileName") {
        return normalized;
    }
    const cleaned = cleanTechnicalValue(normalized);
    return cleaned.length > 0 ? cleaned : "Updated";
};

export const VersionDiffModal = ({
                                     isOpen,
                                     onClose,
                                     areaId,
                                     selectedVersion,
                                     baseVersion,
                                     hasDraftChanges,
                                     canRevert,
                                     onReverted,
                                     onDraftCreated
                                 }: VersionDiffModalProps) => {
    const [diffLoading, setDiffLoading] = useState<boolean>(false);
    const [diffData, setDiffData] = useState<AreaDiff | undefined>(undefined);
    const [diffError, setDiffError] = useState<string | undefined>(undefined);
    const [isReverting, setIsReverting] = useState<boolean>(false);
    const [isMakingDraft, setIsMakingDraft] = useState<boolean>(false);
    const [isMakeDraftPromptOpen, setIsMakeDraftPromptOpen] = useState<boolean>(false);
    const [selectedDiffVersionId, setSelectedDiffVersionId] = useState<string | undefined>(undefined);
    const cancelPromptRef = useRef<HTMLButtonElement>(null);
    const wasOpenRef = useRef<boolean>(false);

    const client = useApolloClient();
    const [createDraftFromVersion] = useMutation(createDraftFromVersionMutation);
    const [revertAreaToPublished] = useMutation(revertAreaToPublishedMutation);
    const {confirm} = useConfirmDialog();
    const {data: versionsData} = useQuery(versionsQuery, {
        variables: {areaId: areaId},
        skip: !isOpen || !areaId
    });

    const allVersions = versionsData?.getVersions || [];
    const draftVersion = allVersions.find((version: Version) => version.publishedAt === 0);
    const publishedVersions = allVersions.filter((version: Version) => version.publishedAt > 0);
    const currentVersion = selectedVersion || baseVersion;
    const isViewingPublishedVersion = !!currentVersion?.publishedAt;
    const compareVersions = isViewingPublishedVersion
        ? [hasDraftChanges ? draftVersion : undefined, ...publishedVersions].filter((version): version is Version => !!version)
        : publishedVersions;
    const fallbackVersionId = isViewingPublishedVersion
        ? (hasDraftChanges ? draftVersion?.id : undefined) || currentVersion?.id
        : baseVersion?.id;

    useEffect(() => {
        if (isOpen && !wasOpenRef.current) {
            setSelectedDiffVersionId(fallbackVersionId);
        }
        wasOpenRef.current = isOpen;
        if (!isOpen) {
            setSelectedDiffVersionId(undefined);
        }
    }, [baseVersion?.id, fallbackVersionId, isOpen, selectedVersion?.id, selectedVersion?.publishedAt]);

    const selectedCompareVersion = compareVersions.find((version: Version) => version.id === selectedDiffVersionId)
        || compareVersions.find((version: Version) => version.id === fallbackVersionId)
        || baseVersion;
    const isSameVersionComparison = !!currentVersion?.id
        && !!selectedCompareVersion?.id
        && currentVersion.id === selectedCompareVersion.id;
    const selectedCompareAt = selectedCompareVersion ? (selectedCompareVersion.publishedAt || 0) : undefined;
    const currentCompareAt = currentVersion ? (currentVersion.publishedAt || 0) : undefined;
    const currentVersionLabel = currentVersion?.publishedAt
        ? (selectedVersion?.publishedAt ? "Current selection" : "Latest published version")
        : "Draft";

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setDiffData(undefined);
        setDiffError(undefined);
        setDiffLoading(false);

        if (!currentVersion || !selectedCompareVersion) {
            setDiffError("No published version to compare.");
            return;
        }
        if (!areaId) {
            setDiffError("No space selected.");
            return;
        }
        if (isSameVersionComparison) {
            setDiffData({
                publishedAt: selectedCompareAt,
                spotsAdded: [],
                spotsRemoved: [],
                spotsModified: [],
                areaChanges: [],
                zoneChanges: []
            });
            return;
        }

        const loadDiff = async () => {
            setDiffLoading(true);
            try {
                const response = await client.query({
                    query: areaDiffQuery,
                    variables: {
                        areaId: areaId,
                        publishedAt: selectedCompareAt,
                        compareAt: currentCompareAt
                    },
                    fetchPolicy: "no-cache"
                });
                const diff = response.data?.getAreaDiff;
                if (!diff) {
                    setDiffError("Unable to load diff.");
                    return;
                }
                if (diff.error) {
                    setDiffError(diff.error);
                    return;
                }
                setDiffData(diff);
            } catch (error) {
                console.error(error);
                setDiffError("Unable to load diff.");
            } finally {
                setDiffLoading(false);
            }
        };

        loadDiff();
    }, [isOpen, areaId, currentCompareAt, currentVersion, client, isSameVersionComparison, selectedCompareAt, selectedCompareVersion]);

    const spotsAdded = diffData?.spotsAdded || [];
    const spotsRemoved = diffData?.spotsRemoved || [];
    const spotsModified = diffData?.spotsModified || [];
    const areaChanges = diffData?.areaChanges || [];
    const zoneChanges = diffData?.zoneChanges || [];
    const selectedVersionNote = selectedCompareVersion?.publishedAt
        ? (selectedCompareVersion.releaseNote || "").trim()
        : "";
    const emptyStateLabel = isSameVersionComparison ? "Same version" : "No changes";

    const headers: Header[] = [
        {title: "Change"},
        {title: "Item"},
        {title: "Field"},
        {title: "Selected version"},
        {title: currentVersionLabel}
    ];

    const rows: RowData[] = [];
    let rowIndex = 0;
    const formatVersionOptionLabel = (version: Version) => {
        if (version.publishedAt === 0) {
            return "Draft";
        }
        const baseLabel = moment(new Date(version.publishedAt)).format(DATE_FORMAT_DA);
        const isLatestPublishedVersion = !draftVersion && !!publishedVersions[0] && publishedVersions[0].id === version.id;
        return isLatestPublishedVersion ? `${baseLabel} (Latest published)` : baseLabel;
    };
    const currentVersionSummary = currentVersion
        ? `${currentVersionLabel.toLowerCase()} (${formatVersionOptionLabel(currentVersion)})`
        : currentVersionLabel.toLowerCase();

    spotsAdded.forEach((spot) => {
        rows.push({
            id: `added-${rowIndex++}`,
            fields: [
                {data: "Added"},
                {data: spot.name || "Unnamed spot"},
                {data: "—"},
                {data: "—"},
                {data: "—"}
            ]
        });
    });

    spotsRemoved.forEach((spot) => {
        rows.push({
            id: `removed-${rowIndex++}`,
            fields: [
                {data: "Removed"},
                {data: spot.name || "Unnamed spot"},
                {data: "—"},
                {data: "—"},
                {data: "—"}
            ]
        });
    });

    spotsModified.forEach((spot) => {
        const itemLabel = spot.name || "Unnamed spot";
        spot.changes.forEach((diff) => {
            if (shouldHideDiff(diff.path)) {
                return;
            }
            const displayValues = getDisplayDiffValues(diff, spot.changes);
            rows.push({
                id: `spot-${spot.key}-${rowIndex++}`,
                fields: [
                    {data: "Changed"},
                    {data: itemLabel},
                    {data: getFieldLabel(diff.path)},
                    {data: formatDiffValue(displayValues.before, diff.path)},
                    {data: formatDiffValue(displayValues.after, diff.path)}
                ]
            });
        });
    });

    areaChanges.forEach((diff) => {
        if (shouldHideDiff(diff.path)) {
            return;
        }
        const displayValues = getDisplayDiffValues(diff, areaChanges);
        rows.push({
            id: `area-${rowIndex++}`,
            fields: [
                {data: "Changed"},
                {data: "Space settings"},
                {data: getFieldLabel(diff.path)},
                {data: formatDiffValue(displayValues.before, diff.path)},
                {data: formatDiffValue(displayValues.after, diff.path)}
            ]
        });
    });

    zoneChanges.forEach((zone) => {
        const zoneLabel = zone.name || ZONE_LABELS[zone.zoneId] || "Zone";
        zone.changes.forEach((diff) => {
            if (shouldHideDiff(diff.path)) {
                return;
            }
            const displayValues = getDisplayDiffValues(diff, zone.changes);
            rows.push({
                id: `zone-${zone.zoneId}-${rowIndex++}`,
                fields: [
                    {data: "Changed"},
                    {data: `Zone: ${zoneLabel}`},
                    {data: getFieldLabel(diff.path)},
                    {data: formatDiffValue(displayValues.before, diff.path)},
                    {data: formatDiffValue(displayValues.after, diff.path)}
                ]
            });
        });
    });

    const hasDiffs = rows.length > 0;
    const needsDraftPrompt = hasDraftChanges;

    const onRevert = async () => {
        if (!areaId) {
            return;
        }
        const approved = await confirm({
            title: "Revert all changes",
            body: "This will discard all draft changes and restore the latest published version.",
            confirmText: "Revert",
            isDestructive: true
        });
        if (!approved) {
            return;
        }
        setIsReverting(true);
        setDiffError(undefined);
        try {
            await revertAreaToPublished({
                variables: {
                    areaId: areaId
                }
            });
            onClose();
            if (onReverted) {
                onReverted();
            }
        } catch (error) {
            console.error(error);
            setDiffError("Unable to revert changes.");
        } finally {
            setIsReverting(false);
        }
    };

    const handleMakeDraft = () => {
        if (needsDraftPrompt) {
            setIsMakeDraftPromptOpen(true);
            return;
        }
        makeDraftFromSelected(false);
    };

    const makeDraftFromSelected = async (shouldPublish: boolean) => {
        if (!areaId || !selectedCompareVersion?.publishedAt) {
            return;
        }
        setIsMakingDraft(true);
        setDiffError(undefined);
        try {
            const draftResult = await createDraftFromVersion({
                variables: {
                    areaId,
                    publishedAt: selectedCompareVersion.publishedAt,
                    publishExistingDraft: shouldPublish
                }
            });
            onClose();
            if (onDraftCreated) {
                onDraftCreated(draftResult.data?.createDraftFromVersion);
            }
        } catch (error) {
            console.error(error);
            setDiffError("Unable to make draft from selected version.");
        } finally {
            setIsMakingDraft(false);
            setIsMakeDraftPromptOpen(false);
        }
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size={"6xl"} isCentered>
                <ModalOverlay/>
                <ModalContent maxW={"90vw"} height={"90vh"}>
                    <ModalHeader>Version changes</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody height={"100%"} overflowY={"hidden"} display={"flex"} flexDirection={"column"} minH={0}>
                        <Stack spacing={3} marginBottom={4} flexShrink={0}>
                            <Flex alignItems={"end"} gap={3}>
                                <Box minW={"240px"} maxW={"360px"}>
                                    <Text fontSize={"xs"} fontWeight={"semibold"} color={"gray.600"} marginBottom={1}>
                                        Compare against version
                                    </Text>
                                    <Select
                                        size={"sm"}
                                        value={selectedCompareVersion?.id || ""}
                                        onChange={(event) => setSelectedDiffVersionId(event.target.value)}
                                        maxW={"360px"}
                                    >
                                        {compareVersions.map((version: Version) => {
                                            return (
                                                <option key={version.id} value={version.id}>
                                                    {formatVersionOptionLabel(version)}
                                                </option>
                                            );
                                        })}
                                    </Select>
                                </Box>
                                {selectedVersionNote ? (
                                    <Tooltip label={selectedVersionNote} hasArrow>
                                        <Box flex={1} minW={0}>
                                            <Text fontSize={"xs"} fontWeight={"semibold"} color={"gray.600"}
                                                  marginBottom={1}>
                                                Selected version note
                                            </Text>
                                            <Text
                                                fontSize={"sm"}
                                                overflow={"hidden"}
                                                textOverflow={"ellipsis"}
                                                whiteSpace={"nowrap"}
                                            >
                                                {selectedVersionNote}
                                            </Text>
                                        </Box>
                                    </Tooltip>
                                ) : null}
                            </Flex>
                        </Stack>
                        {diffLoading ? (
                            <Flex alignItems={"center"} justifyContent={"center"} minHeight={"160px"}>
                                <Spinner size={"sm"} mr={3}/>
                                <Text fontSize={"sm"} color={"gray.600"}>Loading changes...</Text>
                            </Flex>
                        ) : diffError ? (
                            <Text fontSize={"sm"} color={"red.600"}>{diffError}</Text>
                        ) : diffData ? (
                            <Stack spacing={4} flex={1} minH={0}>
                                {!hasDiffs ? (
                                    <Text fontSize={"sm"} color={"gray.600"}>{emptyStateLabel}</Text>
                                ) : (
                                    <>
                                        <Text fontSize={"sm"} color={"gray.600"}>
                                            Comparing {currentVersionSummary} to selected version
                                            ({selectedCompareVersion
                                            ? formatVersionOptionLabel(selectedCompareVersion)
                                            : "unknown"})
                                        </Text>
                                        <Flex flex={1} minH={0}>
                                            <TableInternal
                                                headers={headers}
                                                rows={rows}
                                                containerProps={{height: "100%", overflowY: "auto"}}
                                            />
                                        </Flex>
                                    </>
                                )}
                            </Stack>
                        ) : (
                            <Text fontSize={"sm"} color={"gray.600"}>{emptyStateLabel}</Text>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Tooltip label={"Make this version the new draft"}>
                            <Button
                                size={"sm"}
                                variant={"outline"}
                                onClick={handleMakeDraft}
                                isLoading={isMakingDraft}
                                isDisabled={!selectedCompareVersion?.publishedAt || !hasDiffs}
                                mr={3}
                            >
                                Make draft
                            </Button>
                        </Tooltip>
                        {canRevert && hasDiffs ? (
                            <Button
                                size={"sm"}
                                colorScheme={"red"}
                                variant={"outline"}
                                onClick={onRevert}
                                isLoading={isReverting}
                                mr={3}
                            >
                                Revert all changes
                            </Button>
                        ) : null}
                        <Button size={"sm"} onClick={onClose}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <AlertDialog
                isOpen={isMakeDraftPromptOpen}
                leastDestructiveRef={cancelPromptRef}
                onClose={() => setIsMakeDraftPromptOpen(false)}
                isCentered
            >
                <AlertDialogOverlay/>
                <AlertDialogContent borderRadius="lg" boxShadow="xl" border="1px solid" borderColor="gray.200">
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                        Publish current draft?
                    </AlertDialogHeader>
                    <AlertDialogBody>
                        A draft already exists with changes. Should it be published before overwriting it?
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelPromptRef} onClick={() => setIsMakeDraftPromptOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => makeDraftFromSelected(false)}
                            ml={3}
                        >
                            Overwrite
                        </Button>
                        <Button
                            colorScheme="blue"
                            onClick={() => makeDraftFromSelected(true)}
                            ml={3}
                        >
                            Publish & overwrite
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
